import { Injectable, Injector } from '@angular/core';
import { GetSigninUrl } from '../interfaces/services/user.interface';
import { environment } from 'src/environments/environment';
import { ResolveAuthToken } from '../interfaces/services/token.interface';
import { AuthRequest } from '../models/auth-request.model';
import { Observable, throwError, BehaviorSubject, Subject, of } from 'rxjs';
import { HttpService } from '../http.service';
import { AuthResponse } from '../models/AuthResponse';
import { map, tap, catchError, delay } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { CloudVendor } from '../models/cloud-vendor.model';
import { Router } from '@angular/router';
import { IsAuthenticated } from '../interfaces/services/user.interface';
import { Logout } from '../interfaces/services/user.interface';
import { CloudProfileService } from './cloud-profile.service';
import { GetToken } from '../interfaces/services/token.interface';
import { CloudNotificationService } from './cloud-notification.service';
import { CloudNotificationStyle } from '../models/cloud-notification-style.model';
import { RegisterAccount } from '../interfaces/services/user.interface';
import { RegisterAccountRequest } from '../models/register-account-request.model';
import { CloudRefreshService } from './cloud-refresh.service';
import Helper from '../Util/helper';
import { CloudSubscriptionService } from '../cloud-subscription/services/cloud-subscription.service';
import { LogoutEvent } from '../enums/logout-event.enum';
import { StorageKeys } from '../Util/storage-keys';
import { LocalStorageService } from './localstorage.service';
import { Idle } from '@ng-idle/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthUtils } from '../Util/auth-utils';

@Injectable({
  providedIn: 'root'
})
export class CloudAuthenticationService implements GetSigninUrl, ResolveAuthToken, IsAuthenticated, Logout, GetToken, RegisterAccount {

  authenticationStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public logoutEvent: Subject<LogoutEvent> = new Subject<LogoutEvent>();
  public loginEvent: Subject<any> = new Subject<any>();

  constructor(private http: HttpService,
    private readonly modalSvc: NgbModal,
    private readonly localStorage: LocalStorageService,
    private router: Router,
    private profileSvc: CloudProfileService,
    private notificationSvc: CloudNotificationService,
    private readonly idle:Idle,
    private injector: Injector) {
  }

  registerAccount({ user, company, secondUser, vendor, authStateId }): Observable<any> {
    const req = new RegisterAccountRequest();
    req.user = user;
    req.company = company;
    req.secondContactDetails = secondUser;
    req.authStateId = authStateId;
    req.vendor = vendor;

    return this.http.post<AuthResponse>(environment.api.routes.register_user, req).pipe(
      tap(value => {
        this.localStorage.removeItem(environment.constants.token);
        this.localStorage.removeItem(environment.constants.tokenExpiration);
        if (value !== null) {
          this.saveAuthResponse(value);
        }
      }),
      map(value => {
        if (value) {
          return true;
        }
        return false;
      }),
      catchError(error => {
        return this.handleError(error, req.vendor, true);
      })
    );
  }

  checkSecondaryUser(email: any): Observable<any> {
    return this.http.get(environment.api.routes.check_secondary_user, { email });
  }

  getUserIdleTime() {
    return this.http.get<number>(environment.api.routes.get_user_idle_time);
  }

  getSignInUrl(vendorName: string): string {
    //var url = this.router.url + '/auth/login?vendorname=' + vendorName;
    return '/auth/login?vendor=' + vendorName;
  }

  resolveAuthToken(req: AuthRequest, isNewUser: boolean = false): Observable<boolean> {
    return this.http.post<AuthResponse>(environment.api.routes.login_authentication, req).pipe(
      tap((value : AuthResponse) => {

        this.localStorage.removeItem(environment.constants.token);
        this.localStorage.removeItem(environment.constants.tokenExpiration);
        this.localStorage.removeItem(environment.constants.vendor);


        if (value !== null) {
          this.saveAuthResponse(value);
          this.storeEmail(req)
          this.notificationSvc.createToastNotification('auth', 'You have been logged in.', CloudNotificationStyle.success);
        }

        //this.injector.get(CloudSubscriptionService).activatePendingSubscriptions().subscribe();
        this.injector.get(CloudRefreshService).startRefreshService();

        this.loginEvent.next(null);
      }),
      map(value => {
        if (value) {
          return true;
        }
        return false;
      })
      // ,catchError(error => {
      //   return this.handleError(error, null, true, req.code, isNewUser);
      // })
    );
  }

  private storeEmail(req:AuthRequest){
    var {email,password} = AuthUtils.decodeAuthCode(req.code)
    var encodedEmail = AuthUtils.encodeToBase64(email);
    this.localStorage.setItem(environment.constants.emailKey,encodedEmail)
  }

  saveAuthResponse(res: AuthResponse): boolean {
    this.localStorage.setItem(environment.constants.token, res.rawData);
    this.localStorage.setItem(environment.constants.tokenExpiration, res.validTo.toString());

    const decoded = this.profileSvc.extractUserFromClaims(res.rawData);
    this.authenticationStatus.next(true);
    return true;
  }

  isAuthenticated(): boolean {
    if (this.localStorage.getItem(environment.constants.tokenExpiration) != null) {
      const tokenExpiration = Date.parse(this.localStorage.getItem(environment.constants.tokenExpiration));
      const now = Date.now()
      if (tokenExpiration < now) {
        this.refreshToken().subscribe();

        this.authenticationStatus.next(false);
        return false;
      } else {
        this.profileSvc.extractUserFromClaims(this.localStorage.getItem(environment.constants.token));
        this.authenticationStatus.next(true);
        return true;
      }
    }
    return false;
  }

  refreshToken(): Observable<AuthResponse> {
    const responseToken = new AuthResponse();
    responseToken.rawData = this.localStorage.getItem(environment.constants.token);
    return this.http.post<AuthResponse>(environment.api.routes.refresh_token, responseToken).pipe(
      map(value => {
        if (value == null) {
          this.localStorage.removeItem(environment.constants.token);
          this.localStorage.removeItem(environment.constants.tokenExpiration);
          return value;
        }
        this.saveAuthResponse(value);
        return value;
      })
    );
  }

  getToken(): string {
    const token = this.localStorage.getItem(environment.constants.token);
    return token == null ? null : token;
  }

  logout(redirect:boolean=true, showNotification:boolean=true, stopRefreshService:boolean = true, closeModal: boolean = true): boolean {

    if (this.router.url.startsWith('/subscription/create')) redirect = false;

    if (closeModal) this.modalSvc.dismissAll();
    this.localStorage.removeItem(environment.constants.token);
    this.localStorage.removeItem(environment.constants.tokenExpiration);
    this.localStorage.removeItem(environment.constants.vendor);
    this.localStorage.removeItemSessionStorage(StorageKeys.SubscriptionId);
    this.localStorage.removeItemSessionStorage(StorageKeys.email_path);

    this.localStorage.removeItem('deploymentRedirection');
    this.localStorage.clear();
    this.notificationSvc.panelDisplayStatus.next(false);
    if(redirect) this.router.navigate([environment.api.routes.login]);
    if(stopRefreshService) this.injector.get<CloudRefreshService>(CloudRefreshService).stopRefreshService();
    this.authenticationStatus.next(false);
    if(showNotification)
      this.notificationSvc.createorUpdateToastNotification('auth', 'You have been logged out.', CloudNotificationStyle.success);

    this.logoutEvent.next(LogoutEvent.ClearNotifications);
    this.idle.stop()
    return true;
  }

  localLogout(showNotification = true): boolean {
    this.localStorage.removeItem(environment.constants.token);
    this.localStorage.removeItem(environment.constants.tokenExpiration);
    this.localStorage.removeItemSessionStorage(StorageKeys.SubscriptionId);
    this.localStorage.clear();
    this.authenticationStatus.next(false);
    if (showNotification) {
      this.notificationSvc.createorUpdateToastNotification('auth', 'You have been logged out.', CloudNotificationStyle.success);
    }
    return true;
  }

  private handleError(error: HttpErrorResponse, cloudVendor: CloudVendor, isSubscribing?: boolean, code?: string, isNewUser: boolean = false) {

    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', Helper.resolveErrorMessage(error));
    } else {

      if (error.status === 400) {
        if (Helper.resolveErrorMessage(error) === 'Your password is incorrect.') {
          this.notificationSvc.createToastNotification('auth',
          Helper.resolveErrorMessage(error) + ' Please try again',
           CloudNotificationStyle.danger);
          this.router.navigate(['/auth/login'], { queryParams: { authCode: code } });
        }

        const regex = /(account|email)[a-zA-Z0-9 $!.]*not[a-zA-Z0-9 $!.]*verified/g;
        if (!isNewUser && (Helper.resolveErrorMessage(error) as string).toLowerCase().match(regex)) {
          this.router.navigate(['/auth/login'], { queryParams: { authCode: code, error: 'unverified' } });
          return;
        }

        if (Helper.resolveErrorMessage(error) === 'CryptographicException') {
          this.notificationSvc.createorUpdateToastNotification('auth', "Sorry! We can't log you in at the moment. Please try forgot password.", CloudNotificationStyle.danger);
          this.router.navigate(['/auth/login']);
          return;
        }
      }

      if (error.status === 401) {
        if (isSubscribing) {
          return throwError(error.error);
        }
        this.router.navigate(['register/' + cloudVendor.vendorName + '/' + error.error]);
      }

      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      return throwError(error.error);
    }
    // return an observable with a user-facing error message
    if (!environment.production) {
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${Helper.resolveErrorMessage(error)}`);
    }
  }
}
